import React, { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Grid,
    TextField, Button, Alert, CircularProgress, Paper, Divider, Fade, InputAdornment,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../constants";
import { useBrand } from "../../../contexts/BrandContext";

const CustomerLookupDialog = ({ open, onClose }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || "";
    const [profileId, setProfileId] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const handleSearch = async () => {
        if (!brandName || !profileId) return;
        setLoading(true); setError(null); setResult(null);
        try {
            const res = await fetch(`${LOYALTY_API_BASE}/customers/${encodeURIComponent(brandName)}/${encodeURIComponent(profileId)}/wallet`, {
                headers: { "X-Brand": brandName }
            });
            if (!res.ok) {
                if (res.status === 404) throw new Error("Client introuvable pour cette marque");
                throw new Error(`Erreur HTTP ${res.status}`);
            }
            setResult(await res.json());
        } catch (err) { setError(err.message); } finally { setLoading(false); }
    };

    const handleClose = () => { setProfileId(""); setResult(null); setError(null); onClose(); };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.search.gradient }}>
                        <PersonSearchIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={600} color="#2c3e50">
                        Consulter un Client
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2.5} px={1.5} py={1}
                    sx={{ borderRadius: 2, bgcolor: "#f8f9ff", border: "1px solid #e0e7ff" }}>
                    <StorefrontIcon sx={{ fontSize: 18, color: SECTION_COLORS.search.main }} />
                    <Typography fontSize={13} color="text.secondary">Marque active :</Typography>
                    <Chip label={brandName || "Aucune marque sélectionnée"} size="small"
                        sx={{
                            fontWeight: 700, fontSize: 12,
                            bgcolor: brandName ? `${SECTION_COLORS.search.main}15` : "#fff0f0",
                            color: brandName ? SECTION_COLORS.search.main : "#DC2626"
                        }} />
                </Box>

                {!brandName && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                        Veuillez sélectionner une marque dans la barre de navigation avant de rechercher un client.
                    </Alert>
                )}

                <TextField
                    fullWidth label="Rechercher par Profile ID" value={profileId}
                    onChange={(e) => setProfileId(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && profileId && brandName && handleSearch()}
                    placeholder="Saisissez le Profile ID du client..." size="medium" disabled={!brandName}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: SECTION_COLORS.search.main }} /></InputAdornment> }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 2.5, fontSize: 14, fontFamily: "Montserrat, sans-serif",
                            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: SECTION_COLORS.search.main },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: SECTION_COLORS.search.main, borderWidth: 2 }
                        },
                        "& .MuiInputLabel-root.Mui-focused": { color: SECTION_COLORS.search.main }
                    }}
                />

                {loading && <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} sx={{ color: SECTION_COLORS.search.main }} /></Box>}
                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}

                {result && (
                    <Fade in>
                        <Paper elevation={0} sx={{ mt: 2, p: 2.5, borderRadius: 3, background: "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)", border: "1px solid #e0e7ff" }}>
                            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                                <Avatar sx={{ bgcolor: SECTION_COLORS.search.main, width: 36, height: 36 }}>
                                    {result.profileId?.charAt(0)?.toUpperCase() || "?"}
                                </Avatar>
                                <Box>
                                    <Typography fontWeight={700} fontSize={15} fontFamily="Montserrat, sans-serif">{result.profileId}</Typography>
                                    <Typography fontSize={12} color="text.secondary">{result.brand}</Typography>
                                </Box>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Box textAlign="center">
                                        <AccountBalanceWalletIcon sx={{ color: SECTION_COLORS.tiers.main, fontSize: 28, mb: 0.5 }} />
                                        <Typography fontWeight={700} fontSize={18} color={SECTION_COLORS.tiers.main}>{result.pointsBalance ?? 0}</Typography>
                                        <Typography fontSize={11} color="text.secondary" fontWeight={500}>Points Disponibles</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box textAlign="center">
                                        <EmojiEventsIcon sx={{ color: "#FFD93D", fontSize: 28, mb: 0.5 }} />
                                        <Typography fontWeight={700} fontSize={18} color="#1a1a2e">{result.loyaltyStatus || "–"}</Typography>
                                        <Typography fontSize={11} color="text.secondary" fontWeight={500}>Niveau Fidélité</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box textAlign="center">
                                        <TrendingUpIcon sx={{ color: SECTION_COLORS.rewards.main, fontSize: 28, mb: 0.5 }} />
                                        <Typography fontWeight={700} fontSize={18} color={SECTION_COLORS.rewards.main}>{result.lifetimePoints ?? 0}</Typography>
                                        <Typography fontSize={11} color="text.secondary" fontWeight={500}>Points à Vie</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Box mt={3} display="flex" justifyContent="center">
                                <Button variant="outlined"
                                    onClick={() => {
                                        handleClose();
                                        navigate(`/fidelite/profil/${result.profileId}`);
                                    }}
                                    sx={{
                                        borderRadius: 2, textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                                        borderColor: SECTION_COLORS.search.main, color: SECTION_COLORS.search.main,
                                    }}>
                                    Voir le Profil Fidélité
                                </Button>
                            </Box>
                        </Paper>
                    </Fade>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0" }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary", textTransform: "none" }}>Fermer</Button>
                <Button variant="contained" onClick={handleSearch} disabled={!brandName || !profileId || loading}
                    startIcon={<SearchIcon />}
                    sx={{
                        background: SECTION_COLORS.search.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3,
                        "&:hover": { boxShadow: `0 4px 16px ${SECTION_COLORS.search.main}40` }
                    }}>
                    Rechercher
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomerLookupDialog;
